import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "iacover_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — show manual instructions.
    if (isIOS()) setShow(true);

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (isIOS()) {
      setIosHelp((v) => !v);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      style={{ bottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      className="fixed inset-x-3 z-[60] mx-auto max-w-md rounded-2xl border border-border bg-card shadow-xl shadow-black/10 p-4 animate-in-up"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm">Installer Iacover</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Ajoutez l'app à votre écran d'accueil pour un accès plein écran, comme une vraie app.
          </p>
          {iosHelp && (
            <p className="text-xs text-foreground mt-2 leading-relaxed flex items-center gap-1 flex-wrap">
              Appuyez sur <Share className="w-3.5 h-3.5 inline" /> <span className="font-medium">Partager</span> puis
              <PlusSquare className="w-3.5 h-3.5 inline" /> <span className="font-medium">« Sur l'écran d'accueil »</span>.
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button
              data-testid="install-accept-btn"
              onClick={install}
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition"
            >
              {isIOS() ? "Comment faire ?" : "Installer"}
            </button>
            <button
              data-testid="install-dismiss-btn"
              onClick={dismiss}
              className="h-9 px-3 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted transition"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Fermer"
          className="w-7 h-7 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted transition shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
